import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const SERVICES = [
  "oil_change", "brakes", "diagnostics", "inspection",
  "tires", "transmission", "engine", "battery"
];

export default function AdminService() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", vehicle_info: "",
    service_type: "oil_change", appointment_date: new Date().toISOString().slice(0, 16),
    notes: "", status: "scheduled",
  });

  const load = async () => { const { data } = await api.get("/service-appointments"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/service-appointments", form);
      toast.success("Appointment scheduled");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (it, status) => {
    await api.put(`/service-appointments/${it.id}`, { ...it, status });
    load();
  };

  return (
    <AdminShell title="Service">
      <div className="flex justify-between mb-6">
        <div className="label-eyebrow">{items.length} appointments</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta rounded-sm" data-testid="add-service-btn"><Plus className="w-4 h-4 mr-2" /> Add Appointment</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B1324] border-white/10">
            <DialogHeader><DialogTitle className="font-heading text-xl">Schedule Service</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm">Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" data-testid="service-name-input" /></div>
              <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-sm">Vehicle</Label><Input value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} placeholder="2021 Honda Civic" className="bg-[#030712] border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-sm">Service type</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger className="bg-[#030712] border-white/10 h-10 mt-1" data-testid="service-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Date / time</Label><Input type="datetime-local" value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-sm">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#030712] border-white/10 mt-1" /></div>
              <Button onClick={create} className="btn-cta rounded-sm w-full" data-testid="save-service-btn">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="surface-elev text-left">
            <tr>
              <th className="px-4 py-3 label-eyebrow">Date</th>
              <th className="px-4 py-3 label-eyebrow">Customer</th>
              <th className="px-4 py-3 label-eyebrow">Vehicle</th>
              <th className="px-4 py-3 label-eyebrow">Service</th>
              <th className="px-4 py-3 label-eyebrow">Phone</th>
              <th className="px-4 py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`service-row-${i.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-300">{i.appointment_date?.replace("T", " ").slice(0, 16)}</td>
                <td className="px-4 py-3">{i.customer_name}</td>
                <td className="px-4 py-3 text-slate-400">{i.vehicle_info}</td>
                <td className="px-4 py-3">
                  <span className="pill pill-gold capitalize">{i.service_type?.replace("_", " ")}</span>
                </td>
                <td className="px-4 py-3 text-slate-400">{i.phone}</td>
                <td className="px-4 py-3">
                  <Select value={i.status} onValueChange={(v) => updateStatus(i, v)}>
                    <SelectTrigger className="h-8 w-32 bg-[#04060d] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No appointments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
